import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, PlusCircleOutlined, UserOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Flex from "components/shared-components/Flex";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import { useFirestore } from "react-redux-firebase";
import _ from "lodash";
import * as dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import roles from "roles";
import countryData from "../../../../data/countries/countries.json";
import { useCmsUsers } from "../../../../data/hooks/useCmsUsers";
import { usePermissions } from "../../../../data/hooks/usePermissions";
import {
  PERMISSION_GROUPS,
  ROLE_ORDER,
  SCOPE_ALL,
  SCOPE_COUNTRIES,
  SCOPE_CUSTOMERS,
  SCOPE_NONE,
  getCmsRole,
  getCmsRoleField,
  getEffectivePermissions,
  getRolePermissions,
  getScope,
  isLinkedWebshopAccount,
  normalizeCountries,
} from "../../../../functions/permissions";
import { FIRESTORE_USERS_TABLE } from "../../../../constants/FirestoreConstant";
import { inviteCmsUser } from "../../../../functions/api/cmsAuth";
import { permissionKey } from "../roles";

const { Option } = Select;
const { Text } = Typography;

dayjs.extend(relativeTime);

/** Epoch-seconden of Firestore Timestamp → dayjs, anders null. */
const toDayjs = (value) => {
  if (!value) return null;
  if (typeof value === "number") return dayjs.unix(value);
  if (typeof value.toDate === "function") return dayjs(value.toDate());
  if (typeof value.seconds === "number") return dayjs.unix(value.seconds);
  return null;
};

const renderMoment = (value) => {
  const moment = toDayjs(value);
  return moment ? (
    <Tooltip title={moment.format("DD-MM-YYYY HH:mm")}>
      <span>{moment.fromNow()}</span>
    </Tooltip>
  ) : (
    <span className="text-muted">-</span>
  );
};

/**
 * Beheer → Gebruikers.
 *
 * Twee soorten accounts met CMS-toegang (zie functions/permissions.js):
 * - via uitnodiging aangemaakt: rol staat in `role`;
 * - bestaand webshopaccount met CMS-toegang: rol staat in `cmsRole`, `role`
 *   blijft "webshopUser" zodat webshop-login, klantenlijst en verkopersrapport
 *   blijven werken. Intrekken zet alleen de CMS-velden op null.
 *
 * Toevoegen gaat via de API (POST /api/cms/users/invite, functions/api/cmsAuth.js):
 * die maakt het Firebase-account zonder wachtwoord aan of koppelt het
 * webshopaccount, schrijft het profiel en stuurt de uitnodigingsmail. Inloggen
 * gaat daarna met een code per e-mail; `invitedUsers` bestaat niet meer.
 * Bewerken en intrekken schrijven nog rechtstreeks in Firestore.
 *
 * Per gebruiker instelbaar (BMX-36):
 * - rol (vaste set; rechten per rol in Beheer → Rollen);
 * - zichtbaarheid: geen beperking, landen (`salesCountries`, zelfde veld als
 *   de webshop voor verkopers) óf specifieke klanten (`salesCustomerIds`);
 * - uitzonderingen op de rol: `cmsPermissions: { grant, deny }`.
 * Beheerder: altijd alles, geen zichtbaarheid of uitzonderingen.
 */
const Users = () => {
  const firestore = useFirestore();
  const { t } = useTranslation();
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const cmsUsers = useCmsUsers();
  const webshopUsers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);
  const { roleDocs } = usePermissions();
  const [addUserForm] = Form.useForm();
  const [editUserForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [hiddenUserIds, setHiddenUserIds] = useState([]);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const users = useMemo(
    () =>
      _.orderBy(
        (cmsUsers || []).filter((user) => !hiddenUserIds.includes(user.id)),
        [(user) => (user.email || "").toLowerCase()],
        ["asc"],
      ),
    [cmsUsers, hiddenUserIds],
  );

  const customerOptions = useMemo(
    () =>
      _.orderBy(
        (webshopUsers || []).filter((user) => !user.isSalesPerson),
        [(user) => (user.company?.name || user.email || "").toLowerCase()],
      ).map((user) => ({
        value: user.id,
        label: `${user.company?.name || "-"} | ${user.email || ""}${user.company?.country ? ` (${user.company.country})` : ""}`,
      })),
    [webshopUsers],
  );

  const customerNameById = useMemo(() => _.keyBy(webshopUsers || [], "id"), [webshopUsers]);

  const roleName = (role) => (role ? t(`roles.${role}.name`) : "-");
  const permissionLabel = (permission) => t(`permissions.${permissionKey(permission)}.label`);

  // ---------- formulier (gedeeld door toevoegen en bewerken) ----------

  const rules = {
    email: [{ required: true, type: "email", message: t("form.enter_user_email") }],
    role: [{ required: true, message: t("form.enter_role") }],
    salesCountries: [{ required: true, type: "array", min: 1, message: t("access.enter_sales_countries") }],
    salesCustomerIds: [{ required: true, type: "array", min: 1, message: t("access.enter_sales_customers") }],
  };

  const permissionOptions = (filter) =>
    PERMISSION_GROUPS.map((group) => ({
      label: t(`permission_groups.${group.key}`),
      options: group.permissions.filter(filter).map((permission) => ({
        value: permission,
        label: permissionLabel(permission),
      })),
    })).filter((group) => group.options.length > 0);

  /**
   * De rol-, zichtbaarheids- en uitzonderingsvelden. Als functie en niet als
   * component: een component die binnen render wordt gedefinieerd remount bij
   * elke render en klapt de selects dicht na elke keuze.
   */
  const renderAccessFields = (form, values) => {
    const role = values.role;
    if (!role) return null;
    const isAdminRole = role === roles.admin;
    const rolePermissions = getRolePermissions(role, roleDocs);
    const scopeType = values.scopeType || (role === roles.sales ? SCOPE_COUNTRIES : SCOPE_ALL);
    const effective = getEffectivePermissions(
      { role, cmsPermissions: { grant: values.grant || [], deny: values.deny || [] } },
      roleDocs,
    );

    return (
      <>
        <Text type="secondary" style={{ display: "block", marginTop: -12, marginBottom: 16 }}>
          {t(`roles.${role}.description`)}
        </Text>
        {!isAdminRole && (
          <>
            <Divider orientation="left" plain>
              {t("access.scope")}
            </Divider>
            <Form.Item name="scopeType" extra={t("access.scope_help")} initialValue={scopeType}>
              <Radio.Group>
                {role !== roles.sales && <Radio value={SCOPE_ALL}>{t("access.scope_all")}</Radio>}
                <Radio value={SCOPE_COUNTRIES}>{t("access.scope_countries")}</Radio>
                <Radio value={SCOPE_CUSTOMERS}>{t("access.scope_customers")}</Radio>
              </Radio.Group>
            </Form.Item>
            {scopeType === SCOPE_COUNTRIES && (
              <Form.Item
                name="salesCountries"
                label={t("access.sales_countries")}
                rules={rules.salesCountries}
                extra={t("access.sales_countries_help")}
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={t("access.sales_countries")}
                  options={countryData.map((country) => ({
                    value: country.value,
                    label: `${country.label} (${country.value})`,
                  }))}
                />
              </Form.Item>
            )}
            {scopeType === SCOPE_CUSTOMERS && (
              <Form.Item name="salesCustomerIds" label={t("access.sales_customers")} rules={rules.salesCustomerIds}>
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={t("access.sales_customers")}
                  options={customerOptions}
                />
              </Form.Item>
            )}

            <Divider orientation="left" plain>
              {t("access.effective_permissions")}
            </Divider>
            <Form.Item name="grant" label={t("access.extra_permissions")} extra={t("access.extra_permissions_help")}>
              <Select
                mode="multiple"
                allowClear
                optionFilterProp="label"
                options={permissionOptions((p) => !rolePermissions.includes(p))}
              />
            </Form.Item>
            <Form.Item name="deny" label={t("access.denied_permissions")} extra={t("access.denied_permissions_help")}>
              <Select
                mode="multiple"
                allowClear
                optionFilterProp="label"
                options={permissionOptions((p) => rolePermissions.includes(p))}
              />
            </Form.Item>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {effective.map((permission) => (
                <Tag key={permission}>{permissionLabel(permission)}</Tag>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  /** Formulierwaarden → velden op het profiel / de uitnodiging. */
  const buildAccessFields = (values) => {
    const role = values.role;
    const isAdminRole = role === roles.admin;
    const scopeType = isAdminRole
      ? SCOPE_ALL
      : values.scopeType || (role === roles.sales ? SCOPE_COUNTRIES : SCOPE_ALL);
    const salesCountries = scopeType === SCOPE_COUNTRIES ? normalizeCountries(values.salesCountries) : [];
    const salesCustomerIds = scopeType === SCOPE_CUSTOMERS ? _.uniq(values.salesCustomerIds || []) : [];
    const grant = isAdminRole ? [] : _.uniq(values.grant || []);
    const deny = isAdminRole ? [] : _.uniq(values.deny || []);
    return {
      salesCountries: salesCountries.length > 0 ? salesCountries : null,
      salesCustomerIds: salesCustomerIds.length > 0 ? salesCustomerIds : null,
      cmsPermissions: grant.length > 0 || deny.length > 0 ? { grant, deny } : null,
    };
  };

  const formValuesFromUser = (user) => {
    const scope = getScope(user);
    return {
      editEmail: user.email,
      role: getCmsRole(user),
      scopeType: scope.type === SCOPE_NONE ? SCOPE_COUNTRIES : scope.type,
      salesCountries: normalizeCountries(user.salesCountries),
      salesCustomerIds: Array.isArray(user.salesCustomerIds) ? user.salesCustomerIds : [],
      grant: user.cmsPermissions?.grant || [],
      deny: user.cmsPermissions?.deny || [],
    };
  };

  // ---------- acties ----------

  const openEditModal = (user) => {
    setUserToEdit(user);
    editUserForm.setFieldsValue(formValuesFromUser(user));
    setEditUserModalVisible(true);
  };

  const closeAddModal = () => {
    setAddUserModalVisible(false);
    addUserForm.resetFields();
  };

  const closeEditModal = () => {
    setEditUserModalVisible(false);
    setUserToEdit(null);
    editUserForm.resetFields();
  };

  const editUser = () => {
    editUserForm.validateFields().then((values) => {
      const update = {
        [getCmsRoleField(userToEdit)]: values.role,
        ...buildAccessFields(values),
      };
      setSaving(true);
      firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(userToEdit.id)
        .update(update)
        .then(() => {
          closeEditModal();
          messageApi.success(t("access.user_saved"), 2);
        })
        .catch((err) => messageApi.error(err.message))
        .finally(() => setSaving(false));
    });
  };

  const deleteUser = (user) => {
    const linked = isLinkedWebshopAccount(user);
    // Gekoppeld webshopaccount: alleen de CMS-velden weg; `salesCountries`
    // blijft staan omdat de webshop dat veld zelf gebruikt voor verkopers.
    const update = linked
      ? { cmsRole: null, cmsPermissions: null, salesCustomerIds: null }
      : { role: null, status: "deleted" };
    firestore
      .collection(FIRESTORE_USERS_TABLE)
      .doc(user.id)
      .update(update)
      .then(() => {
        setHiddenUserIds((ids) => [...ids, user.id]);
        messageApi.success(linked ? t("access.revoked") : t("user_deleted"), 2);
      })
      .catch((err) => messageApi.error(err.message));
  };

  /** Uitnodigen of koppelen via de API; `linked` zegt welke van de twee het werd. */
  const submitInvite = async (email, role, accessFields) => {
    setSaving(true);
    try {
      const result = await inviteCmsUser({ email, role, ..._.omitBy(accessFields, _.isNull) });
      messageApi.success(result?.linked ? t("access.granted") : t("access.invite_sent", { email }), 5);
      closeAddModal();
    } catch (error) {
      if (error?.code === "ALREADY_CMS_USER") messageApi.error(t("user_already_exists"), 4);
      else if (error?.code === "FORBIDDEN") messageApi.error(t("access.invite_forbidden"), 4);
      else messageApi.error(t("access.invite_failed", { message: error?.message || "" }), 6);
    } finally {
      setSaving(false);
    }
  };

  const linkExistingWebshopUser = (email, role, accessFields) => {
    Modal.confirm({
      title: t("access.link_existing_title"),
      content: t("access.link_existing_description", { email, role: roleName(role) }),
      okText: t("access.link_existing_ok"),
      cancelText: t("cancel"),
      onOk: () => submitInvite(email, role, accessFields),
    });
  };

  const handleInviteUser = () => {
    addUserForm.validateFields().then((values) => {
      const email = (values.email || "").trim().toLowerCase();
      const accessFields = buildAccessFields(values);
      const cmsUserExists = _.find(cmsUsers, (user) => (user.email || "").toLowerCase() === email);
      const webshopUser = _.find(webshopUsers, (user) => (user.email || "").toLowerCase() === email);
      if (!!cmsUserExists) {
        messageApi.error({ content: t("user_already_exists"), duration: 4 });
      } else if (!!webshopUser) {
        linkExistingWebshopUser(email, values.role, accessFields);
      } else {
        submitInvite(email, values.role, accessFields);
      }
    });
  };

  // ---------- tabel ----------

  const renderScope = (user) => {
    const scope = getScope(user);
    if (getCmsRole(user) === roles.admin || scope.type === SCOPE_ALL) {
      return <span className="text-muted">{t("access.all_countries")}</span>;
    }
    if (scope.type === SCOPE_COUNTRIES) return scope.countries.map((code) => <Tag key={code}>{code}</Tag>);
    if (scope.type === SCOPE_CUSTOMERS) {
      const names = scope.customerIds.map((id) => customerNameById[id]?.company?.name || id);
      return (
        <Tooltip title={names.join(", ")}>
          <Tag color="purple">{t("access.scoped_hint_customers", { count: scope.customerIds.length })}</Tag>
        </Tooltip>
      );
    }
    return <Tag color="red">{t("access.scope_countries")}: -</Tag>;
  };

  /**
   * Kolom "Effectieve rechten": teller met alle rechten die deze gebruiker écht
   * heeft, en in de tooltip de volledige lijst per groep. Een beheerder kent de
   * rolrechten niet uit zijn hoofd, dus niet alleen de afwijkingen tonen.
   * Extra rechten (grant) krijgen een plus, uitgesloten rechten (deny) staan
   * doorgestreept, de rest komt uit de rol.
   */
  const renderOverrides = (user) => {
    const role = getCmsRole(user);
    if (!role) return null;
    const effective = getEffectivePermissions(user, roleDocs);
    if (role === roles.admin) {
      return (
        <Tooltip title={t("roles.admin_locked")}>
          <Tag>{t("access.permission_count", { count: effective.length })}</Tag>
        </Tooltip>
      );
    }
    const grant = user.cmsPermissions?.grant || [];
    const deny = user.cmsPermissions?.deny || [];
    const rolePermissions = getRolePermissions(role, roleDocs);
    const hasOverrides = grant.length > 0 || deny.length > 0;

    const tooltip = (
      <div style={{ maxWidth: 360 }}>
        {PERMISSION_GROUPS.map((group) => {
          const rows = group.permissions.filter(
            (permission) =>
              effective.includes(permission) || (deny.includes(permission) && rolePermissions.includes(permission)),
          );
          if (rows.length === 0) return null;
          return (
            <div key={group.key} style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 600, opacity: 0.85 }}>{t(`permission_groups.${group.key}`)}</div>
              {rows.map((permission) => {
                const denied = deny.includes(permission);
                const granted = !denied && grant.includes(permission) && !rolePermissions.includes(permission);
                return (
                  <div
                    key={permission}
                    style={{
                      paddingLeft: 8,
                      textDecoration: denied ? "line-through" : "none",
                      opacity: denied ? 0.6 : 1,
                    }}
                  >
                    {granted ? "+ " : denied ? "− " : "• "}
                    {permissionLabel(permission)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );

    return (
      <Tooltip title={tooltip} overlayStyle={{ maxWidth: 400 }}>
        <span style={{ cursor: "help" }}>
          <Tag color={hasOverrides ? "blue" : "default"}>
            {t("access.permission_count", { count: effective.length })}
          </Tag>
          {grant.length > 0 && <Tag color="green">+{grant.length}</Tag>}
          {deny.length > 0 && <Tag color="red">−{deny.length}</Tag>}
        </span>
      </Tooltip>
    );
  };

  const tableColumns = [
    {
      title: t("user"),
      dataIndex: "email",
      sorter: { compare: (a, b) => (a.email || "").localeCompare(b.email || "") },
      render: (email, row) => (
        <Flex alignItems="center" style={{ gap: 8 }}>
          <span>{email}</span>
          {isLinkedWebshopAccount(row) && (
            <Tooltip title={t("access.linked_webshop_account_tooltip")}>
              <Tag color="lime" style={{ marginInlineEnd: 0 }}>
                {t("access.linked_webshop_account")}
              </Tag>
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: t("role"),
      render: (_value, row) => <span>{roleName(getCmsRole(row))}</span>,
      sorter: { compare: (a, b) => (getCmsRole(a) || "").localeCompare(getCmsRole(b) || "") },
    },
    {
      title: t("access.scope"),
      render: (_value, row) => renderScope(row),
    },
    {
      title: t("access.effective_permissions"),
      render: (_value, row) => renderOverrides(row),
    },
    {
      title: t("created_at"),
      // Nieuwe CMS-accounts: createdAt (epoch). Oudere en gekoppelde accounts: created_at (Timestamp).
      render: (_value, row) => renderMoment(row.createdAt || row.created_at),
      sorter: {
        compare: (a, b) =>
          (toDayjs(a.createdAt || a.created_at)?.unix() || 0) - (toDayjs(b.createdAt || b.created_at)?.unix() || 0),
      },
    },
    {
      title: t("last_login_timestamp"),
      render: (_value, row) => renderMoment(row.lastLoginTimestamp),
      sorter: {
        compare: (a, b) => (a.lastLoginTimestamp || 0) - (b.lastLoginTimestamp || 0),
      },
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_value, row) => (
        <div className="text-right">
          <EllipsisDropdown
            menu={
              <Menu>
                <Menu.Item
                  onClick={() => {
                    if (row.id !== auth.uid) openEditModal(row);
                    else messageApi.warning(t("notifications.cannot_edit_self"), 4);
                  }}
                >
                  <Flex alignItems="center">
                    <EditOutlined />
                    <span style={{ marginLeft: 4 }}>{t("edit")}</span>
                  </Flex>
                </Menu.Item>
                {row.id !== auth.uid && (
                  <Menu.Item>
                    <Flex alignItems="center">
                      <DeleteOutlined />
                      <Popconfirm
                        placement="bottom"
                        title={
                          isLinkedWebshopAccount(row)
                            ? t("access.confirm_revoke", { email: row.email })
                            : t("confirm_delete_user", { email: row.email })
                        }
                        onConfirm={() => deleteUser(row)}
                        okText={t("yes")}
                        cancelText={t("no")}
                      >
                        <span style={{ marginLeft: 4 }}>{t("delete")}</span>
                      </Popconfirm>
                    </Flex>
                  </Menu.Item>
                )}
              </Menu>
            }
          />
        </div>
      ),
    },
  ];

  const roleSelect = (
    <Select style={{ width: 240 }}>
      {ROLE_ORDER.map((role) => (
        <Option key={role} value={role}>
          {roleName(role)}
        </Option>
      ))}
    </Select>
  );

  return (
    <>
      {contextHolder}
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <Button onClick={() => setAddUserModalVisible(true)} type="primary" icon={<PlusCircleOutlined />}>
            {t("add_user")}
          </Button>
        </div>
        <div className="table-responsive">
          <Table columns={tableColumns} dataSource={users} rowKey="id" />
        </div>

        <Modal
          title={t("add_user")}
          open={addUserModalVisible}
          onCancel={closeAddModal}
          onOk={handleInviteUser}
          okText={t("invite")}
          cancelText={t("close")}
          width={720}
          confirmLoading={saving}
          destroyOnClose
        >
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            {t("access.invite_help")}
          </Text>
          <Form
            layout="vertical"
            form={addUserForm}
            name="add_cms_user"
            initialValues={{ role: roles.user, salesCountries: [], salesCustomerIds: [], grant: [], deny: [] }}
          >
            <Form.Item name="email" label={t("email")} rules={rules.email}>
              <Input
                placeholder={t("email")}
                prefix={<UserOutlined className="site-form-item-icon" />}
                suffix={
                  <Tooltip title={t("form.add_user_email_tooltip")}>
                    <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
                  </Tooltip>
                }
              />
            </Form.Item>
            <Form.Item name="role" label={t("role")} rules={rules.role}>
              {roleSelect}
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {() => renderAccessFields(addUserForm, addUserForm.getFieldsValue(true))}
            </Form.Item>
          </Form>
        </Modal>

        {editUserModalVisible && (
          <Modal
            title={t("edit_user", { name: userToEdit.email })}
            open={editUserModalVisible}
            onCancel={closeEditModal}
            onOk={editUser}
            okText={t("save")}
            cancelText={t("close")}
            confirmLoading={saving}
            width={720}
          >
            <Form layout="vertical" form={editUserForm} name="edit_cms_user">
              <Form.Item name="editEmail" label={t("email")}>
                <Input disabled prefix={<UserOutlined className="site-form-item-icon" />} />
              </Form.Item>
              <Form.Item name="role" label={t("role")} rules={rules.role}>
                {roleSelect}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => renderAccessFields(editUserForm, editUserForm.getFieldsValue(true))}
              </Form.Item>
            </Form>
          </Modal>
        )}
      </Card>
    </>
  );
};

export default Users;
